import { format } from 'date-fns';
import { Suspense, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Navigate, Route, Routes, useMatch } from 'react-router-dom';

import { useAnalytics } from 'contexts/analytics/AnalyticsProvider';
import { useAnalyticsSource } from 'contexts/analytics/useAnalyticsSource';

import useCurrentKitAndTinyAccount from 'hooks/useCurrentKitAndTinyAccount';
import { useMarkSurfaceSeen } from 'hooks/useNewUpdateIndicator';
import { usePrewarmAiChat } from 'hooks/usePrewarmChat';
import { getTinyAccountById } from 'store/account';
import {
  getKitById,
  getKitsForTinyAccountId,
  getLatestKitForTinyAccountId,
  getResultsReadyKitForTinyAccountId,
} from 'store/kits';
import { getKits } from 'store/shared';
import { getImpersonateId } from 'store/user';

import {
  kitResultsViewable,
  sortKitsByResultsViewableOrLatest,
} from '@repo/utils';
import APImage from 'assets/images/action-card.jpg';
import {
  Card,
  ConsultWidget,
  ContainerSizes,
  Header,
  Icon,
  OrderKitButton,
  PageContainer,
  Spinfinity,
  Typography,
} from 'components';
import { TinyPlusUpsellCard } from 'components/TinyPlusUpsellCard/TinyPlusUpsellCard';
import TwoColumnGrid from 'components/TwoColumnGrid/TwoColumnGrid';
import VersionPill from 'components/VersionPill/VersionPill';
import { parseDate } from 'helpers';
import EmptyActionPlan from 'views/ActionPlan/EmptyActionPlan';
import { TINY_PLUS_UPSELL_IMAGES } from 'views/Home/HomeContent/RecommendedForYou/utils/getTinyPlusUpsellCard';
import ActionPlanCategories from './ActionPlanCategories';
import { ActionPlanDetail } from './ActionPlanDetail';
import ActionPlanDisclaimer from './ActionPlanDisclaimer';
import { ActionRelatedInsights } from './ActionRelatedInsights/ActionRelatedInsights';
import PartnerRecsCard from './PartnerRecsCard/PartnerRecsCard';
import PendingResultsBanner from './PendingResultsBanner';
import PractitionerNote from './PractitionerNote/PractitionerNote';

import styles from './ActionPlan.module.css';

const BlogCard = () => {
  return (
    <Card
      variant='wrapper'
      clickable={{
        text: `How to Use Your Action Plan`,
        href: `https://www.tinyhealth.com/blog/a-guide-to-making-the-most-of-your-microbiome-action-plan`,
        variety: 'Card',
        isExternal: true,
      }}
    >
      <div className={styles.actionPlanCard}>
        <div className={styles.actionPlanCardContent}>
          <div className={styles.actionPlanCardHeader}>
            <Typography variant='heading-s'>
              How to Use Your Action Plan
            </Typography>
            <Icon name='help' size='m' />
          </div>
          <Typography variant='body-s' serif>
            If you’ve taken one of our gut or vaginal tests, you’re...
          </Typography>
        </div>
        <div className={styles.actionPlanImageWrapper}>
          <img src={APImage} alt='Action Plan Information' />
        </div>
      </div>
    </Card>
  );
};

export const ActionPlan = () => {
  const resultMatch = useMatch<'kitId', string>('/results/:kitId');
  const actionPlanMatch = useMatch<'kitId', string>('/action-plan/:kitId');
  const impersonateId = useSelector(getImpersonateId);
  const kits = useSelector(getKits);
  const defaultKitId = sortKitsByResultsViewableOrLatest(kits)[0]?.id;
  const currentKitId =
    resultMatch?.params?.kitId || actionPlanMatch?.params?.kitId;
  const currentKit = useSelector(getKitById(currentKitId));
  const currentKitResultsAreViewable = kitResultsViewable(
    currentKit,
    !!impersonateId,
  );
  const currentTinyAccountId = currentKit?.tinyaccount_id ?? null;
  const currentTinyAccount = useSelector(
    getTinyAccountById(currentTinyAccountId),
  );

  // Get the latest kit (by sample date) and results-ready kit for this tinyaccount
  const latestKit = useSelector(
    getLatestKitForTinyAccountId(currentTinyAccountId),
  );
  const resultsReadyKit = useSelector(
    getResultsReadyKitForTinyAccountId(currentTinyAccountId),
  );
  const kitsForAccount = useSelector(
    getKitsForTinyAccountId(currentTinyAccountId),
  );

  const hasOtherKitsWithResults = kitsForAccount.some(
    kit => kit.id !== currentKitId && kitResultsViewable(kit, !!impersonateId),
  );

  useMarkSurfaceSeen(
    currentTinyAccountId ?? null,
    currentKitId ?? null,
    'actionPlan',
  );

  const resultsReadyKitId = resultsReadyKit?.id;

  // Show banner only when:
  // - viewing the most recent results-ready kit
  // - the latest kit is a resample whose results aren't ready yet
  const showPendingBanner =
    currentKitResultsAreViewable &&
    latestKit &&
    currentKitId !== latestKit.id &&
    !!latestKit.is_resample &&
    !latestKit.date_results_ready;

  const latestKitDateFormatted = latestKit?.sample_date
    ? format(parseDate(latestKit.sample_date), 'MMMM d, yyyy')
    : undefined;
  const currentKitDateFormatted = currentKit?.sample_date
    ? format(parseDate(currentKit.sample_date), 'MMMM d, yyyy')
    : undefined;

  const { trackEvent } = useAnalytics();
  const actionPlanSource = useAnalyticsSource();
  useEffect(() => {
    if (!currentKitId) return;
    trackEvent('Action Plan Viewed', { source: actionPlanSource });
  }, [currentKitId, actionPlanSource, trackEvent]);

  let NextAction = null;
  if (!resultMatch && !currentKitId && defaultKitId) {
    // By doing this we avoid unnecessary rendering.
    return <Navigate to={`/action-plan/${defaultKitId}`} replace />;
  } else {
    NextAction = (
      <EmptyActionPlan
        hasOtherKitsWithResults={hasOtherKitsWithResults}
        latestKitDate={latestKitDateFormatted}
        resultsReadyKitId={resultsReadyKitId}
      />
    );
  }

  // Extract the condition to show action plan content vs NextAction
  const showActionPlanContent =
    currentKitResultsAreViewable && !!currentKit?.date_survey_completed;

  return (
    <section id='action_plan'>
      <Header
        type='full'
        title={`${currentTinyAccount?.first_name}'s Action Plan`}
      />
      <PageContainer size={ContainerSizes.MAIN}>
        <TwoColumnGrid>
          <TwoColumnGrid.Wide>
            <div className={styles.actionPlanWrap}>
              {showPendingBanner &&
                latestKitDateFormatted &&
                currentKitDateFormatted && (
                  <PendingResultsBanner
                    latestKitDate={latestKitDateFormatted}
                    currentKitDate={currentKitDateFormatted}
                  />
                )}
              <Suspense fallback={<Spinfinity />}>
                {showActionPlanContent ? (
                  <div className='flex flex-column gap-3'>
                    <div className={'hide-on-desktop mb-3'}>
                      <PractitionerNote />
                    </div>
                    <PartnerRecsCard className={'hide-on-desktop'} />
                    <ConsultWidget
                      title='Need help with your action plan?'
                      source='action_plan'
                    />
                    <Suspense fallback={<Spinfinity />}>
                      <ActionPlanCategories kitId={currentKitId} />
                      {currentKit?.sci_initial_version && (
                        <div className='flex gap-2 mt-3'>
                          <Typography variant='label' className='my-auto'>
                            System version
                          </Typography>
                          <VersionPill shadow />
                        </div>
                      )}
                    </Suspense>
                    {currentKit && !!resultMatch && (
                      <OrderKitButton classNames={'pt-4'}>
                        Order your next kit
                      </OrderKitButton>
                    )}
                  </div>
                ) : (
                  <Suspense fallback={<Spinfinity />}>{NextAction}</Suspense>
                )}
              </Suspense>
            </div>
          </TwoColumnGrid.Wide>
          <TwoColumnGrid.Slim isSticky>
            <div className={styles.sideColumn}>
              {showActionPlanContent && (
                <div className={'hide-on-mobile'}>
                  <PractitionerNote />
                </div>
              )}
              <BlogCard />
              <PartnerRecsCard className={'hide-on-mobile'} />
              {showActionPlanContent && (
                <TinyPlusUpsellCard
                  title='Know how your body is responding to your Action Plan by retesting.'
                  label={<>Tiny+ members save $80 on resampling kits.</>}
                  imgStyles={TINY_PLUS_UPSELL_IMAGES.graph}
                  className={styles.upsellCard}
                  titleContainerClassName={styles.upsellContent}
                  leftColumnStyles={styles.upsellLeftColumn}
                  titleVariantMobile='heading-m'
                  labelVariantMobile='label'
                  kit={currentKit}
                  placement='action_plan'
                />
              )}
            </div>
          </TwoColumnGrid.Slim>
        </TwoColumnGrid>
        {showActionPlanContent && <ActionPlanDisclaimer />}
      </PageContainer>
    </section>
  );
};

export const ActionPlanRoutes = () => {
  const { currentKitId } = useCurrentKitAndTinyAccount();
  usePrewarmAiChat(currentKitId);
  return (
    <Routes>
      <Route
        path='/action/:actionId/insights'
        element={<ActionRelatedInsights />}
      />
      <Route path='/action/:actionId' element={<ActionPlanDetail />} />{' '}
      <Route
        path={`/action`}
        key='insights'
        element={<Navigate to={`/action-plan/${currentKitId}`} replace />}
      />
      <Route path='' element={<ActionPlan />} />
    </Routes>
  );
};
